import { type FC } from 'react';

import { IcoMoonIcon } from '@wwe-portal/ui/components/icons';
import { cn } from '@wwe-portal/ui/lib/lib';

import { useMilestone } from './hooks';
import { MilestoneItemIcon } from './milestone-item-icon';
import type { MilestonePeriodProps } from './milestone-period';
import TooltipWrapper from './tooltip';
import { shortenNumber } from '@wwe-portal/utils/number';

const MilestonePeriodV2: FC<MilestonePeriodProps> = ({
  data: {
    id,
    periodType = 1,
    currentPoint,
    endPoint,
    imgUrl,
    currencyType,
    isClaimed = false,
    endTime,
    updatedAt = Date.now(),
    quantity,
  },
  onReset,
}) => {
  const { timeLeft, displayPoint } = useMilestone({
    data: { periodType, currentPoint, endTime, updatedAt },
    onReset,
  });

  if (new Date(endTime).getTime() < new Date().getTime()) return null;

  const percent = Math.round((displayPoint / endPoint) * 100);

  return (
    <div
      className="font-poppins text-text300 bg-back400 relative h-[190px] w-[314px] pr-1 text-xs font-[400] leading-5"
      id={id}
    >
      <div className="flex flex-col">
        <div className="mt-2 flex flex-col items-center">
          <MilestoneItemIcon
            className="size-16"
            currencyType={currencyType}
            displayPoint={displayPoint}
            imgUrl={imgUrl}
            isClaimed={isClaimed}
          />
          <span className="stroke-2 text-base">&#215;{quantity}</span>
        </div>

        <div className="w-[80%] self-center" data-enabled>
          <div className="relative cursor-pointer py-4">
            <div className="bg-back200 rounded-1 absolute h-2 w-[100%] rounded" />
            <div
              className="relative h-[8px] rounded bg-gradient-to-r from-[#FC8154] via-[#FFA800] via-purple-500 to-[#FFBB00] to-pink-500"
              style={{
                width: `${percent > 0 ? percent : 3}%`,
              }}
            >
              <div
                className={cn(
                  'absolute right-[5px] top-[2px] h-1 w-1 rounded-full blur-[4px]',
                  {
                    'right-[2px]': percent < 3,
                  }
                )}
                style={{
                  boxShadow: '0 0 0 4px #FFBB00',
                }}
              />
              <div
                className={cn(
                  'absolute right-[5px] top-[2px] h-[3px] w-[3px] rounded-full bg-white',
                  {
                    'right-[2px]': percent < 3,
                  }
                )}
              />
            </div>
            <span className="absolute -right-4 -top-4">
              <TooltipWrapper
                content={
                  <span>
                    {displayPoint}/{endPoint} XP
                  </span>
                }
              >
                <>
                  <span className="text-yellow800">
                    {shortenNumber(displayPoint)}
                  </span>
                  /{shortenNumber(endPoint)}
                </>
              </TooltipWrapper>
            </span>
          </div>
        </div>
      </div>

      <div className="bg-back200 text-text400 absolute bottom-0 flex h-[58px] w-full items-center px-4">
        <IcoMoonIcon className="text-yellow800 mr-1 size-4" iconName="timer" />
        {Boolean(timeLeft) && <span>{timeLeft} left</span>}
      </div>
    </div>
  );
};

export default MilestonePeriodV2;
